<template>
  <div class="campaign-dashboard d-flex flex-column gap-3">
    <div class="row d-flex align-items-center">
      <div class="campaign-dashboard--header d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <h4 class="convey-dashboard--page-title mb-0">
            {{ currentCampaign ? currentCampaign.campaign_name : 'Convey Alert Dashboard' }}
          </h4>
          <DashboardToggle @showDashboard="showDashboard" />
        </div>
        <ConveyDatePicker
          v-if="userDashboardView"
          placeholder="Select Date Range"
          @changeDatePicker="handleChangeDatePicker"
          :isDarkMode="isDarkMode"
          :currentDateRange="dateRange"
        />
      </div>
    </div>
    <b-overlay :show="isLoading || isLoadingDataByCampaignId" rounded="sm" no-wrap />
    <div v-if="userDashboardView" class="campaign-dashboard--blocks row d-flex">
      <div class="col-md-12 col-sm-12 col-lg-12 col-xl-6">
        <CampaignStatistics :isDarkMode="isDarkMode" :statsCount="statsCount" />
      </div>
      <div class="col-md-12 col-sm-12 col-lg-6 col-xl-6 mb-3">
        <SubscriberLanguageGraph
          :isDarkMode="isDarkMode"
          :subscriberLanguageData="subscriberLanguageData"
          :isLoading="isLoading"
        />
      </div>

      <div
        class="col-md-12 col-sm-12 col-lg-6 col-xl-6 mb-3"
        style="height: 450px"
      >
        <CampaignActivityGraph
          :isDarkMode="isDarkMode"
          :campaignActivityData="campaignActivity"
        />
      </div>
      <div
        class="col-md-12 col-sm-12 col-lg-12 col-xl-6 mb-3"
        style="height: 450px"
      >
        <SubscriberActivityGraph
          :isDarkMode="isDarkMode"
          :SubscribersActivityData="SubscribersActivity"
        />
      </div>
    </div>
    <div v-else class="campaign-dashboard--simplified d-flex align-items-center justify-content-center px-3 py-3">
      <img
        class="app-logo"
        :src="agencyLogo"
        alt="Convey911 Agency Logo"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import CampaignStatistics from "./CampaignStatistics.vue";
import SubscriberLanguageGraph from "./SubscriberLanguageGraph.vue";
import CampaignActivityGraph from "./CampaignActivityGraph.vue";
import SubscriberActivityGraph from "./SubscriberActivityGraph.vue";
import ConveyDatePicker from "@/components/common/DatePicker.vue";
import DashboardToggle from "../../common/DashboardToggle.vue";
import { mapGetters } from "vuex";
import {
  campaignStatsQuery,
  makeCampaignEventsQuery,
  makeCampaignSubscribersQuery,
  makegCampaignSubscribersTotalStatsQuery
} from "@/graphql/queries";
import { runGraphqlQuery } from "@/graphql/utils";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";

import moment from "moment";
export default {
  name: "CampaignDashboard",
  components: {
    CampaignStatistics,
    SubscriberLanguageGraph,
    CampaignActivityGraph,
    SubscriberActivityGraph,
    ConveyDatePicker,
    DashboardToggle,
  },
  mixins: [formatter, errorHandler],
  data() {
    return {
      selectedTimeFrame: { value: "today", label: "Today" },
      options: [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "last7Days", label: "Last 7 Days" },
        { value: "last30Days", label: "Last 30 Days" },
        { value: "thisMonth", label: "This Month" },
        { value: "thisYear", label: "This Year" },
        { value: "customDate", label: "Custom Date" },
      ],
      statsCount: {
        campaign: null,
        avgCampaignSize: null,
        messages: null,
        undelivered: null,
        newSubscribers: null,
        totalSubscribers: null,
        totalEvents: null,
      },
      subscriberLanguageData: null,
      campaignActivity: null,
      SubscribersActivity: null,
      dateRange: {
        start_date: moment().subtract(29, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      },
      isLoading: false,
      isLoadingDataByCampaignId: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode", "userDashboardView"]),
    ...mapGetters("agency", ["currentAgencyId", "agencyLogo"]),
    ...mapGetters("campaigns", ["currentCampaign"]),
    currentCampaignId() {
      return this.$route.params.campaignId;
    }
  },
  watch: {
    dateRange(newVal, oldVal) {
      if (newVal != oldVal) {
        this.fetchOperations();
      }
    }
  },
  created() {
    if (this.userDashboardView) {
      this.fetchOperations();
    }
  },
  methods: {
    showDashboard(value) {
      if (value) {
        this.fetchOperations();
      } else {
        this.abortFetchOperations();
      }
    },
    abortFetchOperations() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    fetchOperations() {
      this.fetchAbortController = new AbortController();
      this.fetchAllData();
      this.fetchDataByGivenCampaignId();
    },
    handleChangeDatePicker(data) {
      if (data.start && data.end) {
        this.dateRange = {
          start_date: data.start,
          end_date: data.end,
        };
      } else {
        this.dateRange = null;
      }
    },
    async fetchAllData() {
      try {
        this.isLoading = true;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }

        const query = campaignStatsQuery(startDate, endDate);
        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getCampaignStats;
        this.statsCount = {
          ...this.statsCount,
          campaign: this.formatNumber(responseData.totalCount),
          avgCampaignSize: this.formatDuration(
            responseData.averageCampaignDuration
          ),
          messages: {
            totalCount: this.formatNumber(responseData.totalMessagesCount),
          },
          undelivered: {
            totalCount: this.formatNumber(responseData.undeliveredPercentage),
          },
          newSubscribers: {
            totalCount: responseData.newSubscriberStats
              ? this.formatNumber(responseData.newSubscriberStats.totalCount)
              : 0,
            graphData: responseData.newSubscriberStats
              ? responseData.newSubscriberStats.statsData
              : [],
          },
        };
        this.subscriberLanguageData = responseData.subscriberLanguageStats;
        this.campaignActivity = responseData.messagesChartStats
          ? {
              ...responseData.messagesChartStats,
              totalCount: this.formatNumber(
                responseData.messagesChartStats.totalCount
              ),
            }
          : null;
        this.SubscribersActivity = responseData.subscribersChartStats.map(item => {
          const { x_axis } = item;
          const engagement = responseData.subscriberEngagementStats.find(i => i.x_axis === x_axis);
          return {
            ...item,
            ...engagement,
          };
        });
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[CampaignDashboard:fetchAllData] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDataByGivenCampaignId() {
      try {
        this.isLoadingDataByCampaignId = false;
        let startDate = moment().startOf("year").format("YYYY-MM-DD");
        let endDate = moment().endOf("year").format("YYYY-MM-DD");

        if (this.dateRange && this.dateRange.start_date) {
          startDate = this.dateRange.start_date;
          endDate = this.dateRange.end_date;
        }
        const queryParams = {
          offset: 0,
          perPage: 10,
          campaignId: this.currentCampaignId,
          searchKey: "",
          filter: [],
          startDate,
          endDate,
          orderBy: "created_at",
          sortDesc: false,
        };
        const totalSubscribersQuery = makegCampaignSubscribersTotalStatsQuery({ campaignId: this.currentCampaignId });
        const totalEventsQuery = makeCampaignEventsQuery(queryParams);
        const { data: totalSubscribersResponse } = await runGraphqlQuery(totalSubscribersQuery, this.fetchAbortController.signal);
        const { data: totalEventsResponse } = await runGraphqlQuery(totalEventsQuery, this.fetchAbortController.signal);
        this.statsCount = {
          ...this.statsCount,
          totalSubscribers: Number(totalSubscribersResponse.data.getCampaignSubscribersTotalStats.total),
          totalPendingSubscribers: Number(totalSubscribersResponse.data.getCampaignSubscribersTotalStats.pending),
          totalVerifiedSubscribers: Number(totalSubscribersResponse.data.getCampaignSubscribersTotalStats.verified),
          totalOptOutSubscribers: Number(totalSubscribersResponse.data.getCampaignSubscribersTotalStats.optout),
          totalEvents: Number(totalEventsResponse.data.getCampaignEvents.total),
        };
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[CampaignDashboard:fetchDataByGivenCampaignId] ERROR: ", err);
        }
      } finally {
        this.isLoadingDataByCampaignId = false;
      }
    }
  },
  beforeDestroy() {
    this.abortFetchOperations();
  }
};
</script>


<style lang="scss">
.campaign-dashboard {
  height: 100%;
  &--blocks {
    &.row {
      margin: 0 -0.5rem !important;
      > * {
        padding: 0 0.5rem !important;
      }
    }
  }
  &--page-title {
    color: #000000;
    font-size: 1.25rem;
    font-weight: 500;
  }
  &--simplified {
    flex: 1;
  }
}
</style>